import React from 'react';
import { browserHistory, withRouter, Link } from 'react-router';
import { Helmet } from 'react-helmet';
import queryString from 'query-string';
import classnames from 'classnames';
import Analytics from '../../services/analytics';

import Api from '../../api/api';
import AppStore from '../../stores/stores';
import AppConstants from '../../constants/constants';
import AuthMixin from '../../mixins/authenticationMixin';
import DataError from '../../components/errors/data-error';
import PostHogService from '../../services/posthog';
import PostHogEvents from '../../constants/posthog-events';
import AppRoutes from '../../routes/routes';
import './search-marketing.scss';

class SearchMarketing extends React.Component {
    constructor() {
        super();
        this.state = {
            noData: false,
            data: [],
            totalShowing: '',
            totalCount: '',
            totalPages: '',
            recentSearches: [],
            error: false
        };
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.query.q !== this.props.location.query.q ||
            prevProps.location.query.page !== this.props.location.query.page ||
            prevProps.location.query.filter !== this.props.location.query.filter) {
            this._onChange(this.props);
        }
    }

    componentDidMount() {
        this._onChange(this.props);
    }

    _onChange(props) {
        const { userApiRequest, location } = props;
        const { query } = location;

        AppStore.getSearchResults(query, userApiRequest, {
            onSuccess: ({ data }) => {
                this.setState({
                    error: false,
                    data: data.data,
                    noData: (data.data.length) ? false : true,
                    totalShowing: data.total_showing,
                    totalCount: data.total_count,
                    totalPages: data.total_pages
                });
                setTimeout(Api.scrollCentre);

                Analytics.event('search', { search_term: query.q, result_count: data.total_count });

                PostHogService.capture(PostHogEvents.SEARCH_PERFORMED, {
                    query: query.q,
                    result_count: data.total_count,
                });
            },
            onError: () => this.setState({ error: true })
        });

        this.getRecentSearches(props);
    }

    getRecentSearches(props) {
        AppStore.getRecentSearches(props.userApiRequest, {
            onSuccess: ({ data }) => {
                this.setState({
                    error: false,
                    recentSearches: data.recent_searches || [],
                });
            },
            onError: error => this.setState({ error: true })
        });
    }

    switchFilters(name) {
        const { query, pathname: url } = this.props.location;
        const queryParams = queryString.stringify({
            q: query.q,
            page: AppConstants.DEFAULTSEARCHPAGE,
            per_page: AppConstants.PERPAGE,
            filter: name
        });

        Analytics.event('search_filter', { filter: name });

        PostHogService.capture(PostHogEvents.SEARCH_FILTER_CLICKED, {
            filter: name,
            query: query.q
        });

        browserHistory.replace(`${url}?${queryParams}`);
    }

    renderResultItem(result) {
        const query = this.props.location.query.q;
        const { title, type, slug } = result;
        let url = (type === "checklist") ? `${AppRoutes.checklists}${slug}` : `${AppRoutes.explore}?open=${slug}`;

        const trackClick = () => {
            PostHogService.capture(PostHogEvents.SEARCH_RESULT_CLICKED, {
                title: title,
                type: type,
                slug: slug,
                query: query
            });
        };

        return (
            <Link
                key={`${type}-${slug}`}
                to={{ pathname: url, query: { "highlight-text": query } }}
                className="search-result-card"
                onClick={trackClick}
            >
                <div className="result-meta">
                    <span className="result-type-badge">{type}</span>
                </div>
                <h3>{title}</h3>
                {result.excerpt && <p className="result-excerpt">{result.excerpt}</p>}
            </Link>
        );
    }

    renderPagination() {
        const { location } = this.props;
        const { totalPages } = this.state;
        const { query, pathname: url } = location;
        const pageNum = parseInt(query.page || 1);

        if (totalPages <= 1) return null;

        const nextPageQuery = Object.assign({}, query, { page: pageNum + 1 });
        const prevPageQuery = Object.assign({}, query, { page: pageNum - 1 });

        return (
            <div className="marketing-pagination">
                {pageNum > 1 ? (
                    <Link to={{ pathname: url, query: prevPageQuery }} className="nav-btn prev">
                        <span>&#10094; Previous</span>
                        <small>Page {pageNum - 1} of {totalPages}</small>
                    </Link>
                ) : <div />}

                {pageNum < totalPages ? (
                    <Link to={{ pathname: url, query: nextPageQuery }} className="nav-btn next">
                        <span>Next &#10095;</span>
                        <small>Page {pageNum + 1} of {totalPages}</small>
                    </Link>
                ) : <div />}
            </div>
        );
    }

    render() {
        if (this.state.error) return <DataError />;

        const { query } = this.props.location;
        const filter = query.filter || 'all';
        const filterItems = ['all', 'checklists', 'topics', 'chapters'];
        const searchTitle = query && query.q ? query.q : '';

        return (
            <div className="search-marketing-page">
                <Helmet>
                    <title>Search Results: {searchTitle} | Neurochecklists</title>
                </Helmet>

                <header className="m-checklist-header pure-g">
                    <div className="inner-container inner-container--wide pure-u-1">
                        <div className="header-top">
                            <div>
                                <p className="last-updated" style={{ marginBottom: '8px', opacity: 0.8 }}>Search Results for:</p>
                                <h1>"{searchTitle}"</h1>
                                <p className="last-updated" style={{ marginTop: '12px' }}>
                                    Showing {this.state.totalCount || 0} results found in our database
                                </p>
                            </div>
                        </div>
                    </div>
                </header>

                <div className="main-content">
                    <div className="pure-g">
                        <div className="pure-u-1 pure-u-md-16-24">
                            <div className="search-filter-bar">
                                <ul>
                                    {filterItems.map(name => (
                                        <li
                                            key={name}
                                            className={classnames({ selected: filter.toLowerCase() === name })}
                                            onClick={() => this.switchFilters(name)}
                                        >
                                            {name}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="search-results-container">
                                {this.state.noData ? (
                                    <div className="no-results">
                                        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                            <circle cx="11" cy="11" r="8"></circle>
                                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                        </svg>
                                        <h3>No matching results</h3>
                                        <p>We couldn't find anything matching "{searchTitle}". Try checking your spelling or using more general terms.</p>
                                    </div>
                                ) : (
                                    this.state.data.map(result => this.renderResultItem(result))
                                )}
                            </div>

                            {this.renderPagination()}
                        </div>

                        <aside className="pure-u-1 pure-u-md-8-24 mobile-hide-ib">
                            <div style={{ paddingLeft: '40px' }} className="search-sidebar-marketing">
                                {this.state.recentSearches.filter(item => item && item.title && item._slugs && item._slugs[0]).length > 0 && (
                                    <div className="sidebar-section">
                                        <h4>Recent Searches</h4>
                                        <div className="search-results-container" style={{ gap: '8px' }}>
                                            {this.state.recentSearches
                                                .filter(item => item && item.title && item._slugs && item._slugs[0])
                                                .slice(0, 5)
                                                .map((item, idx) => (
                                                    <Link
                                                        key={idx}
                                                        to={`${AppRoutes.checklists}${item._slugs[0]}`}
                                                        className="search-result-card"
                                                        style={{ padding: '12px 16px', borderRadius: '12px' }}
                                                    >
                                                        <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--body)' }}>{item.title}</span>
                                                    </Link>
                                                ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        );
    }
}

export default AuthMixin(withRouter(SearchMarketing));
